import * as React from 'react';
import { DocumentType } from '../../../operations/createDocument/DocumentType';
import { folderFacet } from '../../../facets/ItemFacets';
import { createDocumentActionControl } from '../../../controls/createDocumentActionControl';
import { withWizard, IDecorateWithWizardProps } from '../../decorators/withWizard';
import { DocumentCreator, IDocumentCreatorProps } from './DocumentCreator';
import { withOnlyCurrentItem, IDecorateWithOnlyCurrentItemProps } from '../../decorators/selectors';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { IActionProps } from '../../action/Action';
import { INameDialogProps } from '../../nameDialog/NameDialog';
import { getItemActionEngagement } from '../../../models/telemetry/getItemEngagement';

/**
 * @public
 */
export interface ICreateDocumentActionBaseProps {
    itemKey: string;
    documentType: DocumentType;
}

/**
 * @public
 */
export type ICreateDocumentWizardProps = IDecorateWithWizardProps<IDocumentCreatorProps>;

const CreateDocumentWizard = withWizard()(DocumentCreator);

/**
 * @public
 */
export type ICreateDocumentActionProps = IDecorateWithOnlyCurrentItemProps<
    ICreateDocumentActionBaseProps & Pick<ICreateDocumentWizardProps, 'templateItemKey'>>;

/**
 * @public
 */
export const CreateDocumentAction: React.ComponentType<ICreateDocumentActionProps> = withOnlyCurrentItem()(
    connectToItemCache((
        itemCache: IItemCache,
        props: ICreateDocumentActionBaseProps
    ): IActionProps &
        Pick<IDocumentCreatorProps, 'askForFileName' | 'openWindow'> &
        Pick<INameDialogProps, 'validator'> => {
        const {
            itemKey,
            documentType
        } = props;

        const folder = itemKey && itemCache.demandItemFacet(folderFacet, itemKey);
        const controlHandler = itemCache.demandItemFacet(createDocumentActionControl, itemKey);

        // For certain document types, we know it always need to ask for file name.
        const needToAskForFileName = documentType === DocumentType.OneNote ||
            documentType === DocumentType.ExcelSurvey ||
            documentType === DocumentType.ExcelForm;

        const {
            isAvailable,
            askForFileName,
            validator,
            openWindow
        } = createDocumentActionControl.evaluate(controlHandler)(itemCache, {
            itemKey,
            documentType,
            isAvailable: false,
            openWindow: (windowProps: { url?: string; }) => {
                return window.open(windowProps.url || '', '_blank');
            }
        });

        return {
            isAvailable: isAvailable && !!folder,
            ...getItemActionEngagement(itemCache, {
                itemKey,
                engagement: {
                    name: 'CreateDocument',
                    isIntentional: true,
                    extraData: {
                        documentType: DocumentType[documentType]
                    }
                },
            }),
            askForFileName: needToAskForFileName || askForFileName,
            validator: validator,
            openWindow
        };
    })(CreateDocumentWizard)
);
